import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';

const odsItems = [
  {
    number: '5',
    icon: 'ri-women-line',
    title: 'Igualdad de género',
    desc: 'Promueve igualdad de oportunidades para mujeres y hombres en el acceso a servicios y generación de ingresos.',
    color: 'bg-orange-50 border-orange-200',
  },
  {
    number: '8',
    icon: 'ri-briefcase-line',
    title: 'Trabajo decente y crecimiento económico',
    desc: 'Impulsa el empleo independiente y digno mediante el acceso a clientes y herramientas de trabajo.',
    color: 'bg-orange-50 border-orange-200',
  },
  {
    number: '9',
    icon: 'ri-lightbulb-line',
    title: 'Industria, innovación e infraestructura',
    desc: 'Utiliza tecnología para modernizar la contratación de servicios tradicionales.',
    color: 'bg-orange-50 border-orange-200',
  },
  {
    number: '10',
    icon: 'ri-scales-line',
    title: 'Reducción de las desigualdades',
    desc: 'Facilita el acceso a oportunidades económicas a distintos sectores de la población.',
    color: 'bg-orange-50 border-orange-200',
  },
  {
    number: '11',
    icon: 'ri-building-2-line',
    title: 'Ciudades y comunidades sostenibles',
    desc: 'Mejora el acceso a servicios locales, fortaleciendo las comunidades.',
    color: 'bg-orange-50 border-orange-200',
  },
];

const pillars = [
  { icon: 'ri-shield-check-line', label: 'Confianza' },
  { icon: 'ri-eye-line', label: 'Transparencia' },
  { icon: 'ri-door-open-line', label: 'Accesibilidad' },
  { icon: 'ri-smartphone-line', label: 'Tecnología' },
];

const AboutPage = () => {
  useEffect(() => {
    document.title = 'Sobre Task Zone – Misión, Visión y Compromiso con los ODS';
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute('content', 'Conoce la historia, misión, visión y propósito de Task Zone: la plataforma que conecta usuarios con proveedores confiables en Aguascalientes. Comprometidos con la transparencia, legalidad y los ODS de la ONU.');

    const schemaId = 'schema-about';
    if (!document.getElementById(schemaId)) {
      const script = document.createElement('script');
      script.id = schemaId;
      script.type = 'application/ld+json';
      script.text = JSON.stringify({
        '@context': 'https://schema.org',
        '@type': 'AboutPage',
        name: 'Sobre Task Zone',
        url: `${import.meta.env.VITE_SITE_URL || 'https://example.com'}/sobre-task-zone`,
        description: 'Conoce la historia, misión, visión y propósito de Task Zone: plataforma de servicios verificados en Aguascalientes.',
        mainEntity: {
          '@type': 'Organization',
          name: 'Task Zone',
          url: import.meta.env.VITE_SITE_URL || 'https://example.com',
          description: 'Plataforma digital que conecta usuarios con proveedores confiables de servicios en Aguascalientes, México.',
          foundingLocation: {
            '@type': 'Place',
            name: 'Aguascalientes, México',
          },
          areaServed: {
            '@type': 'City',
            name: 'Aguascalientes',
          },
        },
      });
      document.head.appendChild(script);
    }

    return () => {
      const el = document.getElementById(schemaId);
      if (el) el.remove();
    };
  }, []);

  return (
    <div className="min-h-screen bg-white font-sans">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-16 overflow-hidden">
        <div className="relative h-[420px] md:h-[520px] w-full">
          <img
            src="https://public.readdy.ai/ai/img_res/21c3eab824ab1c3d75511c80f42dd81a.jpg"
            alt="Task Zone plataforma"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-black/30 to-black/60" />
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4">
            <span className="inline-block bg-orange-500 text-white text-xs font-semibold uppercase tracking-widest px-4 py-1 rounded-full mb-4">
              Nuestra historia
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4 leading-tight">
              Sobre <span className="text-orange-400">Task Zone!</span>
            </h1>
            <p className="text-white/80 text-base md:text-lg max-w-2xl">
              Conectamos personas con proveedores confiables a través de tecnología, transparencia y seguridad.
            </p>
          </div>
        </div>
      </section>

      {/* Qué es Task Zone */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-orange-500 font-semibold text-sm uppercase tracking-widest">¿Qué es?</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2 mb-5 leading-snug">
              Una plataforma diseñada para conectar con confianza
            </h2>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              Task Zone es una plataforma digital diseñada para conectar a personas que requieren servicios con proveedores confiables en diversas áreas, tales como mantenimiento del hogar, servicios técnicos, cuidado personal, tecnología y más.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed mb-4">
              La plataforma surge como respuesta a una problemática cotidiana: la dificultad de encontrar proveedores confiables, la falta de formalidad en la contratación de servicios y la inseguridad tanto para quienes contratan como para quienes ofrecen su trabajo.
            </p>
            <p className="text-gray-600 text-sm leading-relaxed">
              Task Zone busca transformar esta dinámica mediante el uso de tecnología, ofreciendo un entorno estructurado, transparente y seguro. A través de herramientas como perfiles verificados, sistemas de calificación, chat interno, gestión de solicitudes y seguimiento de servicios, Task Zone facilita una experiencia más organizada y confiable.
            </p>
          </div>
          <div className="relative">
            <img
              src="https://readdy.ai/api/search-image?query=two%20people%20shaking%20hands%20in%20a%20professional%20setting%2C%20warm%20orange%20and%20white%20tones%2C%20trust%20and%20collaboration%20concept%2C%20clean%20modern%20background%20with%20soft%20light&width=600&height=480&seq=about-handshake-01&orientation=portrait"
              alt="Confianza y colaboración"
              className="w-full h-80 object-cover object-top rounded-2xl"
            />
            <div className="absolute -bottom-4 -left-4 bg-orange-500 text-white rounded-xl px-5 py-3 shadow-md">
              <p className="text-2xl font-bold">+500</p>
              <p className="text-xs opacity-90">Proveedores verificados</p>
            </div>
          </div>
        </div>

        {/* Pillars */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mt-16">
          {pillars.map((p) => (
            <div key={p.label} className="flex flex-col items-center bg-orange-50 border border-orange-100 rounded-xl py-6 px-4 text-center">
              <div className="w-12 h-12 flex items-center justify-center bg-orange-500 rounded-full mb-3">
                <i className={`${p.icon} text-white text-xl`} />
              </div>
              <span className="text-gray-800 font-semibold text-sm">{p.label}</span>
            </div>
          ))}
        </div>
      </section>

      {/* Misión */}
      <section className="bg-orange-500 py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-orange-100 font-semibold text-sm uppercase tracking-widest">Nuestra razón de ser</span>
            <h2 className="text-3xl font-bold text-white mt-2">Misión</h2>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
              <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full mb-4">
                <i className="ri-user-heart-line text-orange-500 text-xl" />
              </div>
              <h3 className="text-white font-bold text-lg mb-3">Para los usuarios</h3>
              <p className="text-orange-100 text-sm leading-relaxed">
                Brindar una plataforma segura, eficiente y accesible que permita encontrar y contratar servicios de manera confiable, con información clara, herramientas de seguimiento y respaldo durante todo el proceso.
              </p>
            </div>
            <div className="bg-white/10 backdrop-blur-sm border border-white/20 rounded-2xl p-8">
              <div className="w-12 h-12 flex items-center justify-center bg-white rounded-full mb-4">
                <i className="ri-tools-line text-orange-500 text-xl" />
              </div>
              <h3 className="text-white font-bold text-lg mb-3">Para los proveedores</h3>
              <p className="text-orange-100 text-sm leading-relaxed">
                Ofrecer un espacio digital que facilite el acceso a nuevas oportunidades laborales, promueva la profesionalización de sus servicios y les permita gestionar su actividad de manera organizada, segura y con mayor visibilidad.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Visión y Propósito */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16 md:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
          <div className="bg-gray-50 rounded-2xl p-8 border border-gray-100">
            <div className="w-12 h-12 flex items-center justify-center bg-orange-100 rounded-full mb-4">
              <i className="ri-eye-2-line text-orange-500 text-xl" />
            </div>
            <h2 className="text-2xl font-bold text-gray-900 mb-4">Visión</h2>
            <p className="text-gray-600 text-sm leading-relaxed">
              Consolidarse como una plataforma líder en la contratación de servicios a nivel nacional, destacándose por su innovación tecnológica, seguridad jurídica y capacidad de generar valor tanto para usuarios como para proveedores, contribuyendo al desarrollo económico y social.
            </p>
          </div>
          <div className="bg-orange-500 rounded-2xl p-8">
            <div className="w-12 h-12 flex items-center justify-center bg-white/20 rounded-full mb-4">
              <i className="ri-focus-3-line text-white text-xl" />
            </div>
            <h2 className="text-2xl font-bold text-white mb-4">Propósito</h2>
            <p className="text-orange-100 text-sm leading-relaxed">
              Transformar la forma en que se contratan servicios, impulsando un modelo basado en la confianza, la transparencia, la accesibilidad y el uso responsable de la tecnología, beneficiando a todas las partes involucradas.
            </p>
          </div>
        </div>
      </section>

      {/* Sobre la creadora */}
      <section className="bg-gray-50 py-16 md:py-20">
        <div className="max-w-5xl mx-auto px-4 sm:px-6">
          <div className="text-center mb-12">
            <span className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Detrás del proyecto</span>
            <h2 className="text-3xl font-bold text-gray-900 mt-2">Sobre la creadora</h2>
          </div>
          <div className="flex flex-col md:flex-row items-center gap-12 bg-white rounded-2xl border border-gray-100 p-8 md:p-12">
            <div className="flex-shrink-0">
              <div className="w-48 h-48 rounded-2xl overflow-hidden border-4 border-orange-100 shadow-md">
                <img
                  src="https://static.readdy.ai/image/e9e586bd4793f176311c78b231428f92/2d1bb1729a68718d3caf733f3e279b2e.jpeg"
                  alt="Creadora de Task Zone"
                  className="w-full h-full object-cover object-top"
                />
              </div>
              <div className="mt-4 text-center">
                <span className="inline-flex items-center gap-1 bg-orange-50 border border-orange-200 text-orange-600 text-xs font-semibold px-3 py-1 rounded-full">
                  <i className="ri-scales-3-line" /> Estudiante de Derecho
                </span>
              </div>
            </div>
            <div className="flex-1">
              <h3 className="text-xl font-bold text-gray-900 mb-4">Una visión jurídica y tecnológica</h3>
              <p className="text-gray-600 text-sm leading-relaxed mb-4">
                Task Zone fue desarrollado por una estudiante de Derecho con el objetivo de crear una plataforma que no solo facilite la contratación de servicios, sino que también incorpore principios de legalidad, protección de datos y seguridad en las relaciones entre particulares.
              </p>
              <p className="text-gray-600 text-sm leading-relaxed mb-6">
                El proyecto integra una visión jurídica y tecnológica, buscando generar un ecosistema digital donde la formalidad, la confianza y la protección de los usuarios sean elementos fundamentales.
              </p>
              <div className="flex flex-wrap gap-3">
                {['Legalidad', 'Protección de datos', 'Seguridad digital', 'Innovación'].map((tag) => (
                  <span key={tag} className="bg-orange-50 text-orange-600 text-xs font-medium px-3 py-1 rounded-full border border-orange-200">
                    {tag}
                  </span>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ODS */}
      <section className="max-w-5xl mx-auto px-4 sm:px-6 py-16 md:py-20">
        <div className="text-center mb-12">
          <span className="text-orange-500 font-semibold text-sm uppercase tracking-widest">Agenda 2030 · ONU</span>
          <h2 className="text-3xl font-bold text-gray-900 mt-2 mb-3">Compromiso con los ODS</h2>
          <p className="text-gray-500 text-sm max-w-xl mx-auto">
            Para Task Zone es verdaderamente importante contribuir al cumplimiento de los Objetivos de Desarrollo Sostenible y a los principios de la Agenda 2030 de las Naciones Unidas.
          </p>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
          {odsItems.map((ods) => (
            <div key={ods.number} className={`rounded-xl border p-6 ${ods.color}`}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-10 h-10 flex items-center justify-center bg-orange-500 rounded-lg flex-shrink-0">
                  <i className={`${ods.icon} text-white text-lg`} />
                </div>
                <span className="text-orange-600 font-bold text-sm">ODS {ods.number}</span>
              </div>
              <h3 className="text-gray-900 font-semibold text-sm mb-2">{ods.title}</h3>
              <p className="text-gray-600 text-xs leading-relaxed">{ods.desc}</p>
            </div>
          ))}
          {/* Filler card for grid alignment */}
          <div className="rounded-xl border border-orange-100 bg-orange-500 p-6 flex flex-col items-center justify-center text-center">
            <i className="ri-global-line text-white text-3xl mb-3" />
            <p className="text-white font-semibold text-sm">Comprometidos con un futuro más justo y sostenible</p>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="bg-orange-500 py-14">
        <div className="max-w-3xl mx-auto px-4 text-center">
          <h2 className="text-2xl md:text-3xl font-bold text-white mb-4">¿Listo para ser parte de Task Zone?</h2>
          <p className="text-orange-100 text-sm mb-8">Únete como usuario o proveedor y forma parte de una comunidad que transforma la contratación de servicios.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/registro-usuario" className="bg-white text-orange-600 font-semibold text-sm px-8 py-3 rounded-lg hover:bg-orange-50 transition-colors whitespace-nowrap cursor-pointer">
              Registrarme como usuario
            </Link>
            <Link to="/registro-proveedor" className="border-2 border-white text-white font-semibold text-sm px-8 py-3 rounded-lg hover:bg-white/10 transition-colors whitespace-nowrap cursor-pointer">
              Ser proveedor
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AboutPage;
